import { useState, useCallback, useMemo, memo, useEffect, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { useSearchParams } from 'react-router-dom';
import { css } from 'styled-components';
import { useQuery } from '@apollo/client';
import type { ColumnDef } from '@tanstack/react-table';
import AddCircle from '@mui/icons-material/AddCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from 'common/ui/Tooltip';
import Dialog from '@mui/material/Dialog';
import { Stack } from 'common/ui/Layout';
import GenericSnackBar from 'common/GenericSnackBar/GenericSnackBar';

import { Button } from 'common/ui/Button';
import { Colors, Spacings } from 'styles';
import Text from 'common/ui/Text';
import Input from 'common/ui/Input';
import { GET_ALL_HANGING_PROTOCOLS } from 'modules/Apollo/queries';

import { Table } from 'common/Table';
import { toLocaleString } from 'utils/date';
import { DraggableContainer } from 'domains/viewer/DraggableContainer';
import {
  useHangingProtocol,
  invalidateHPInitializationCache,
  invalidateHPSmidGuard,
  markHangingProtocolDeleted,
  broadcastHPInvalidation,
} from 'domains/viewer/HangingProtocol/useHangingProtocol';
import {
  usePreviewHangingProtocol,
  usePreviewHangingProtocolConfiguration,
} from 'domains/viewer/HangingProtocol/usePreviewHangingProtocol';
import { useSelectedHangingProtocolSmid } from 'domains/viewer/HangingProtocol/useSelectedHangingProtocol';
import type { Layouts } from '../../StudyLoader/viewerLoaderState';
import type {
  GetAllHangingProtocolsQuery,
  GetAllHangingProtocolsQueryVariables,
  GetHangingProtocolMatchQuery,
} from 'generated/graphql';
import { useOnUnload } from 'hooks/useOnUnload';
import { BaseWidget, AccessoryButton } from '../BaseWidget';
import { Menu, MenuItem } from 'common/ui/Menu';
import MoreVert from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { logger } from 'modules/logger';
import BorderColor from '@mui/icons-material/BorderColor';
import Replay from '@mui/icons-material/Replay';
import Refresh from '@mui/icons-material/Refresh';

import { useToasterDispatch } from 'common/ui/Toaster/Toaster';
import { HPConfirmationModal } from 'domains/viewer/HangingProtocol/Steps/HPConfirmationModal';
import {
  PREVIEW_WITH_UNSAVED_CHANGES_MODAL,
  RESET_ALL_MODAL,
  makeDeleteHPModalContent,
} from 'domains/viewer/HangingProtocol/Steps/stepsModalFactory';
import { RESET_ALL_TOAST } from 'domains/viewer/HangingProtocol/Steps/stepsToastFactory';
import {
  createStepToastContentComponent,
  getDefaultHPToastOptions,
} from 'domains/viewer/HangingProtocol/Steps/StepToast';
import { useClinicalStepOperations } from 'domains/viewer/HangingProtocol/hooks/useClinicalStepOperations';
import { useClinicalStepDirtyState } from 'domains/viewer/HangingProtocol/hooks/useClinicalStepDirtyState';
import { useClinicalViewportEditing } from 'domains/viewer/HangingProtocol/hooks/useClinicalViewportEditing';
import { useHPActionShortcuts } from 'domains/viewer/HangingProtocol/hooks/useHPActionShortcuts';
import { useReceiveHPWidgetShortcuts } from 'domains/viewer/HangingProtocol/hpWidgetShortcutRelay';
import { HangingProtocolsForm, type HangingProtocolsFormProps } from './HangingProtocolsForm';
import {
  HangingProtocolsForm as HangingProtocolsFormV4,
  hangingProtocolFormState,
} from './HPv4/HangingProtocolsFormV4';
import { withFormIntent } from './HPv4/hangingProtocolFormHelpers';
import { useSubmitForm } from './hooks/useSubmitForm';
import {
  POWERED_BY_SIRONA_CLASSIFICATIONS,
  SNACKBAR_CONTENT_PROPS,
  HP_MODAL_VARIANTS,
  HANGING_PROTOCOL_PHASE,
  isAtLeastPhase4,
} from './constants';
import type { WidgetMode, SubmitFormIntent, HangingProtocolPhase } from './constants';
import { getSuccessNotification } from './utils/getSuccessNotification';
import { getApplyAfterCreateErrorMessage } from './utils/getApplyAfterCreateErrorMessage';
import { getHangingProtocolErrorMessage } from './utils/getHangingProtocolErrorMessage';
import { PreviewCell } from './PreviewCell';
import { Row } from './Row';
import { useClearSteppedHPState } from 'domains/viewer/HangingProtocol/hooks/useClearSteppedHPState';
import { useViewportStepWriters } from 'domains/viewer/ViewportsConfigurations';
import { makeHPKey } from 'domains/viewer/HangingProtocol/state';
import {
  pendingCreateEntries,
  pendingDeletionEntries,
} from 'domains/viewer/HangingProtocol/clinicalStepStore';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useClearEditHeldHangingProtocol } from 'domains/viewer/HangingProtocol/hooks/useHoldHangingProtocolWithUnsavedEdits';
import { useDeleteHangingProtocol } from './hooks/useDeleteHangingProtocol';
import { FF, useSplitFlag } from 'modules/feature-flags';
import { Sidebar, useCloseSidebar, useSidebarState } from '../../Sidebar';
import { useBaseViewerData } from '../../StudyLoader/useStudies';
import { useViewerId } from 'hooks/useViewerId';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { telemetryContextFor } from 'modules/Apollo/types';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { rootTheme } from 'theme/rootTheme';
import { useNumberOfPriors } from './utils/useNumberOfPriors';
import { getPayloadStudyIds } from './utils/getPayloadStudyIds';
import { OUT_OF_BAND_STUDY_PARAMS } from 'hooks/studyParams';
import { syncParamsFromLiveUrl } from 'utils/searchParams';

export const HANGING_PROTOCOL_SIDEBAR_ID = 'hanging-protocol';
export const HP_URL_PARAM_KEY = 'hp';
export enum HPUrlParam {
  Create = 'create',
  Edit = 'edit',
}

type HangingProtocolItem = GetAllHangingProtocolsQuery['hangingProtocols']['items'][number];

const menuItemCss = css`
  height: 24px;
  line-height: ${Spacings.xxsmall}rem;
`;

const iconCss = css`
  margin-right: ${Spacings.small}rem;
  color: ${Colors.gray10};
  opacity: 0.5;
  width: 16px;
  height: 16px;
`;

const isSubmitFormIntent = (mode: WidgetMode): mode is SubmitFormIntent =>
  mode === HP_MODAL_VARIANTS.SAVE || mode === HP_MODAL_VARIANTS.UPDATE;

type HangingProtocolWidgetProps = Readonly<{
  layouts: Layouts;
  studyDescriptions: Array<string>;
  viewerRef: {
    current: null | HTMLDivElement;
  };
  onFormOpenChange?: (isFormOpen: boolean) => void;
}>;

const isDefault = (
  hangingProtocol: Nullable<
    GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies']
  >
) => hangingProtocol?.smid == null;

const HangingProtocolWidget = ({
  layouts,
  studyDescriptions,
  viewerRef,
  onFormOpenChange,
}: HangingProtocolWidgetProps): React.ReactElement => {
  const [phase] = useSplitFlag<HangingProtocolPhase>(FF.HANGING_PROTOCOL_PHASE);
  const isPhase4 = isAtLeastPhase4(phase);
  const [searchParams, setSearchParams] = useSearchParams();
  const [widgetMode, setWidgetMode] = useState<WidgetMode>(null);
  const [isOpen, setOpen] = useState(false);
  const setHangingProtocolFormIntent = useSetRecoilState(hangingProtocolFormState);
  const [search, setSearch] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);

  const { currentCase } = useCurrentCaseContext();
  const primaryStudy = currentCase?.studies[0];
  const numberOfPriors = useNumberOfPriors({ context: APOLLO_QUERY_CONTEXT });

  const groupStudyIds = useMemo(() => getPayloadStudyIds(currentCase), [currentCase]);

  const { hangingProtocol, loadingHangingProtocol, resetToHP } = useHangingProtocol({
    context: APOLLO_QUERY_CONTEXT,
  });
  const { data: allHangingProtocols, loading } = useQuery<
    GetAllHangingProtocolsQuery,
    GetAllHangingProtocolsQueryVariables
  >(GET_ALL_HANGING_PROTOCOLS, {
    // don't fetch HPs if the widget is not open
    skip: !isOpen,
    context: APOLLO_QUERY_CONTEXT,
  });

  const previewedHangingProtocol = usePreviewHangingProtocolConfiguration();
  const selectedHangingProtocolSmid = useSelectedHangingProtocolSmid();
  const {
    previewHangingProtocol,
    confirmPreview,
    selectHangingProtocol,
    clearPreviewState,
    rejectAndRemovePreview,
    refreshSelectedHangingProtocol,
    clearSelectedHangingProtocol,
    loadingPreview,
  } = usePreviewHangingProtocol({ context: APOLLO_QUERY_CONTEXT });

  const closeSidebar = useCloseSidebar();
  const { isOpen: sidebarIsOpen, activeSidebarId } = useSidebarState();
  const viewingViewportClassifications =
    sidebarIsOpen && activeSidebarId === HANGING_PROTOCOL_SIDEBAR_ID;
  const showSidebarOverlay = viewingViewportClassifications && !isPhase4;

  // Track whether the widget was opened via URL param so the effect can close it
  // when the param is removed (e.g. browser back/forward) without interfering with
  // SEARCH/DELETE modes that don't use URL params.
  const openedViaUrlParam = useRef(false);

  // Derive widget state from URL param (e.g. browser back/forward, direct link)
  useEffect(() => {
    const hpParam = searchParams.get(HP_URL_PARAM_KEY);
    if (hpParam === HPUrlParam.Edit) {
      openedViaUrlParam.current = true;
      setWidgetMode(HP_MODAL_VARIANTS.UPDATE);
      setOpen(true);
      // See the matching write in `open()`: eagerly broadcast the intent (and hpSmid,
      // and a cleared closeSignal -- see withFormIntent's doc comment for why both matter)
      // so a second viewer window's NoticeBox does not race the form's own async init (VX-3589).
      setHangingProtocolFormIntent((prev) =>
        withFormIntent(prev, HP_MODAL_VARIANTS.UPDATE, hangingProtocol?.smid ?? null)
      );
    } else if (hpParam === HPUrlParam.Create) {
      openedViaUrlParam.current = true;
      setWidgetMode(HP_MODAL_VARIANTS.SAVE);
      setOpen(true);
      setHangingProtocolFormIntent((prev) => withFormIntent(prev, HP_MODAL_VARIANTS.SAVE, null));
    } else if (hpParam == null && openedViaUrlParam.current) {
      openedViaUrlParam.current = false;
      setWidgetMode(null);
      setOpen(false);
    }
  }, [searchParams, setHangingProtocolFormIntent, hangingProtocol?.smid]);

  const open = useCallback(
    (mode: WidgetMode) => {
      setWidgetMode(mode);
      setOpen(true);

      // Eagerly broadcast the intent (and hpSmid, and a cleared closeSignal -- see
      // withFormIntent's doc comment) on hangingProtocolFormState in the same tick as opening
      // the sidebar (below, via the `isOpen`/`activeSidebarId` props passed to <Sidebar>). In a
      // second viewer window, `sidebarState` and `hangingProtocolFormState` are separate
      // BroadcastChannel-synced atoms; sidebarState arrives first because it requires no async
      // work, while hangingProtocolFormState previously only got its UPDATE value once
      // HangingProtocolsFormV4 mounted and ran its own init effect. That gap let the second
      // window's NoticeBox render off the still-default "create" value before the real UPDATE
      // broadcast landed (VX-3589). Writing it here removes the gap at the source instead of
      // guessing at a delay on the reading side. DELETE/SEARCH modes intentionally skip this --
      // they don't open the editing form, so there is no new intent to broadcast.
      if (isSubmitFormIntent(mode)) {
        setHangingProtocolFormIntent((prev) =>
          withFormIntent(prev, mode, hangingProtocol?.smid ?? null)
        );
      }

      // Add hp param to URL when entering edit/create mode
      const hpParam =
        mode === HP_MODAL_VARIANTS.UPDATE
          ? HPUrlParam.Edit
          : mode === HP_MODAL_VARIANTS.SAVE
            ? HPUrlParam.Create
            : null;

      if (hpParam) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            syncParamsFromLiveUrl(next, OUT_OF_BAND_STUDY_PARAMS);
            next.set(HP_URL_PARAM_KEY, hpParam);
            return next;
          },
          { replace: true }
        );
      }
    },
    [setSearchParams, setHangingProtocolFormIntent, hangingProtocol]
  );

  const close = useCallback(() => {
    if (!isOpen || primaryStudy == null) {
      return;
    }

    if (phase === HANGING_PROTOCOL_PHASE.THREE || isPhase4) {
      closeSidebar();
    }

    // An async submit can retain the params from before the form's URL update. Always remove
    // the open-state param: checking that captured snapshot can leave a closed form at ?hp=edit.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        syncParamsFromLiveUrl(next, OUT_OF_BAND_STUDY_PARAMS);
        next.delete(HP_URL_PARAM_KEY);
        return next;
      },
      { replace: true }
    );

    setWidgetMode(null);
    setOpen(false);
  }, [closeSidebar, isOpen, phase, isPhase4, primaryStudy, setSearchParams]);

  const [completedMessage, setCompletedMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>(null);
  const unsetError = useCallback(() => setErrorMessage(null), []);

  const [highlightRow, setHighlightRow] = useState<number | null>(null);

  const [isPreviewAccepted, setIsPreviewAccepted] = useState(false);

  const { revertAllStepsToBaseline, flushPendingSaves } = useClinicalStepOperations();
  const { hasAnyDirtyStep } = useClinicalStepDirtyState();

  /**
   * Whether there is clinical step work a preview would destroy.
   *
   * `hasAnyDirtyStep` alone is not that question. `useClinicalStepDirtyState` compares against the
   * baseline and deliberately treats a step with no smid (newly added, not yet persisted) and a step
   * absent from the baseline as clean, so an unsaved add -- or a delete still inside its undo window --
   * reads as no changes at all. Previewing on that basis let the deferred create or delete commit
   * against the protocol the reader had just switched away from, with no confirmation offered.
   *
   * Read imperatively rather than subscribed: these are module-level registries in
   * `clinicalStepStore`, and the only moment their value matters is the click being handled.
   */
  const hasUnsavedStepWork = useCallback(
    () =>
      hasAnyDirtyStep || pendingCreateEntries().length > 0 || pendingDeletionEntries().length > 0,
    [hasAnyDirtyStep]
  );

  // The HP the user asked to preview while unsaved clinical changes were still staged. Held here
  // until they answer the discard confirmation: on accept it is what gets previewed, on cancel it is
  // dropped and no preview runs.
  const [pendingPreviewSmid, setPendingPreviewSmid] = useState<string | null>(null);

  // Every Preview click routes through here so the unsaved-changes guard cannot be bypassed by a new
  // call site. `setIsPreviewAccepted(false)` deliberately stays on the unguarded path: flipping it
  // for a preview the user then cancels would make closing the widget reject an *earlier* preview
  // they had already confirmed.
  const requestPreview = useCallback(
    async (smid: string) => {
      if (hasUnsavedStepWork()) {
        setPendingPreviewSmid(smid);
        return;
      }
      setIsPreviewAccepted(false);

      // A deferred save is a third class, and neither the dirty check nor the pending create/delete
      // registries see it: pressing Save clears the step's dirty state at once and leaves only the
      // PATCH queued for the length of the undo window. So this path is reached with a save still in
      // flight, and the HP-change cleanup that follows a preview clears that queue -- timer and map --
      // without sending anything. The reader was told the step saved, and the old configuration comes
      // back on the next reload.
      //
      // Flushed rather than confirmed, because the user already pressed Save: this is committed
      // intent, not unsaved work to ask about (the same reasoning as handleResetAll, VX-4740). Started
      // before the preview because flushPendingSaves empties its queue synchronously before its first
      // await, which is what puts the mutations beyond the cleanup's reach.
      const flushed = flushPendingSaves();
      previewHangingProtocol(smid);
      await flushed;
    },
    [hasUnsavedStepWork, previewHangingProtocol, flushPendingSaves]
  );

  const columns = useMemo(
    (): ColumnDef<HangingProtocolItem>[] => [
      {
        header: 'Date',
        accessorFn: ({ created }) => toLocaleString(created),
        size: 100,
        maxSize: 100,
        minSize: 100,
      },
      {
        header: 'Name',
        accessorKey: 'name',
        size: 161,
        maxSize: 161,
        minSize: 161,
      },
      {
        header: () => <div>Actions</div>,
        accessorKey: 'actions',
        size: 134,
        maxSize: 134,
        minSize: 134,
        cell: ({ row }) => (
          <PreviewCell
            row={row}
            hangingProtocol={hangingProtocol}
            requestPreview={requestPreview}
            setHighlightRow={setHighlightRow}
            isLoadingPreview={loadingPreview}
          />
        ),
      },
    ],
    [hangingProtocol, loadingPreview, requestPreview]
  );

  const searchItems = useMemo(
    () =>
      widgetMode === HP_MODAL_VARIANTS.SEARCH
        ? (allHangingProtocols?.hangingProtocols?.items || []).filter(
            (item): item is typeof item & { created: NonNullable<typeof item.created> } =>
              item.name.toLowerCase().includes(search.trim().toLowerCase()) && item.created != null
          )
        : [],
    [allHangingProtocols?.hangingProtocols?.items, widgetMode, search]
  );

  useOnUnload(rejectAndRemovePreview);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // The "Create new hanging protocol" tooltip lives on a button nested inside the "Hanging
  // Protocol" tooltip's own reference element. Hovering the button never leaves that outer
  // element's bounding box, so the outer tooltip's own hover trigger has no way to know it should
  // step aside -- both tooltips would otherwise show at once (VX-5041). Tracking both hover
  // states explicitly and driving the outer tooltip's visibility from them (its `visible` prop)
  // sidesteps that: it lets the outer sync immediately when the inner hover ends, instead of
  // waiting for a fresh native mouseenter/mouseleave pair that may never come.
  const [isWidgetHovered, setIsWidgetHovered] = useState(false);
  const [isSaveButtonHovered, setIsSaveButtonHovered] = useState(false);

  const deleteHangingProtocol = useDeleteHangingProtocol({ context: APOLLO_QUERY_CONTEXT });
  const clearSteppedHPState = useClearSteppedHPState();
  const { discardStepViewportOverrides } = useViewportStepWriters();
  const currentCaseId = useCurrentCaseId();
  const clearEditHeldHangingProtocol = useClearEditHeldHangingProtocol(currentCaseId);

  const quickUpdate = useSubmitForm({
    hangingProtocol,
    intent: HP_MODAL_VARIANTS.UPDATE,
    currentCase,
    shouldUpdateClassifications: false,
    numberOfPriors,
    includeLayoutChanges: true,
    layouts,
    studyDescriptions,
    groupedStudies: groupStudyIds,
    context: APOLLO_QUERY_CONTEXT,
    onComplete: (hangingProtocolName) => {
      setCompletedMessage(getSuccessNotification(hangingProtocolName, HP_MODAL_VARIANTS.UPDATE));
    },
    onError: (err) => {
      logger.error(err);
      setErrorMessage(getHangingProtocolErrorMessage(err, HP_MODAL_VARIANTS.UPDATE));
    },
  });

  const { invalidateHangingProtocolMatch, refetch } = useBaseViewerData({
    context: APOLLO_QUERY_CONTEXT,
  });

  const clinicalEditing = useClinicalViewportEditing();
  const { enqueueToast } = useToasterDispatch();

  const handleResetAll = useCallback(async () => {
    setShowResetModal(false);
    // Start the pending deferred saves, but hold the await until just before the refetch below.
    // Only the server read has to be ordered behind them -- the local reverts that follow are pure
    // state updates, and awaiting first would stall the whole reset UI on a network round-trip.
    //
    // Starting them here rather than after the reverts is deliberate: revertAllStepsToBaseline
    // cancels anything still pending, so reverting first would leave the flush with nothing to
    // send and drop a save the user had already been told succeeded. The flush clears its queue
    // synchronously before its first await, so the revert below is a no-op on it.
    //
    // Saves flush; deletions deliberately do not. A save means the user pressed Save and only the
    // network call is deferred, so reverting it would discard committed intent (VX-4740). A pending
    // delete is the opposite: it is an unsaved change the modal promises to revert, so
    // revertAllStepsToBaseline cancels it and puts the step back (VX-4815).
    const flushed = flushPendingSaves();
    revertAllStepsToBaseline();
    clinicalEditing.revertAll();
    // Reload HP from server. The flushed writes have to have landed first, or this read can come
    // back with pre-save data and re-seed the step atoms with it, which is what made an
    // already-saved step reappear as unsaved (VX-4740).
    await flushed;
    await invalidateHangingProtocolMatch();
    invalidateHPInitializationCache();
    invalidateHPSmidGuard();
    await refetch();
    // The cached payload for the selected HP predates the flushed writes, so re-resolve it from the
    // server. Reset All resets *changes*, not the selection -- the selection is an smid and is
    // deliberately left alone here, which is what stops the viewer falling through to the server
    // match and silently putting the original protocol back (VX-4789).
    await refreshSelectedHangingProtocol();
    resetToHP();
    broadcastHPInvalidation();
    enqueueToast(RESET_ALL_TOAST.message, {
      ...getDefaultHPToastOptions(),
      ContentComponent: createStepToastContentComponent({}),
    });
  }, [
    flushPendingSaves,
    revertAllStepsToBaseline,
    clinicalEditing,
    refreshSelectedHangingProtocol,
    invalidateHangingProtocolMatch,
    refetch,
    resetToHP,
    enqueueToast,
  ]);

  // Accepting the discard reverts the outgoing protocol the same way Reset All does -- see the
  // ordering rationale in handleResetAll, which applies verbatim: the deferred saves start before the
  // revert cancels them, and the revert itself needs no await. What this does NOT do is Reset All's
  // refetch/resetToHP dance; the preview that follows re-hangs the viewports anyway, so re-resolving
  // the protocol we are switching away from would be wasted work.
  const handleConfirmDiscardAndPreview = useCallback(async () => {
    const smid = pendingPreviewSmid;
    setPendingPreviewSmid(null);
    if (smid == null) {
      return;
    }
    // Preview first, discard second, and only if a preview actually arrived.
    //
    // The reverse order loses the reader's work for nothing when the fetch fails:
    // `previewHangingProtocol` logs the error and leaves preview state untouched, so a transient
    // network or backend failure would leave them on the original protocol with their unsaved edits
    // already destroyed and no replacement to show for it. Now the discard is the last thing that
    // happens, and it only happens once there is something to discard *in favour of*.
    //
    // Safe against clobbering the preview it just applied: `revertAllStepsToBaseline` publishes into
    // the bucket for the `activeHPKey` this callback closed over, which is the outgoing protocol's,
    // not the previewed one's. The live spec asserts the end state either way -- preview applied,
    // dirty indicator cleared, saved layout restored.
    setIsPreviewAccepted(false);
    const previewed = await previewHangingProtocol(smid);
    if (previewed == null) {
      return;
    }

    const flushed = flushPendingSaves();
    revertAllStepsToBaseline();
    clinicalEditing.revertAll();
    await flushed;
  }, [
    pendingPreviewSmid,
    flushPendingSaves,
    revertAllStepsToBaseline,
    clinicalEditing,
    previewHangingProtocol,
  ]);

  const handleCancelDiscardPreview = useCallback(() => setPendingPreviewSmid(null), []);

  // Keyboard equivalents of the dropdown's menu items. Each mirrors its `onClick` exactly, including
  // closing the dropdown, so a hotkey and a click land in the same place -- Reset in particular
  // still routes through the confirmation modal rather than resetting outright.
  const shouldOpenHangingProtocolsForm = isSubmitFormIntent(widgetMode);
  const isHPFormOpen = shouldOpenHangingProtocolsForm && isOpen;

  /**
   * Any of the widget's surfaces being up: the create/edit form, the browse or delete modal, or the Reset All
   * confirmation. Each shortcut below switches mode or resets, so firing one now would swap the surface underneath the
   * reader -- replacing a delete confirmation outright, or opening Browse behind an active Reset All -- and skip the
   * confirm-or-cancel the surface exists to ask for. These are mutually exclusive by construction, so a shortcut
   * declines rather than trying to close one and open another.
   */
  const isHPSurfaceOpen = (widgetMode != null && isOpen) || showResetModal;

  /**
   * VX-4248: previewing overlays a candidate protocol that has not been confirmed. Every dropdown
   * action (Create/Edit/Delete/Reset All/Browse/Update layout) reads or writes the *applied*
   * protocol, so acting on one while a different one is only tentatively on screen is exactly the
   * bug that ticket reports -- Edit, in particular, activated the previewed protocol immediately and
   * Cancel afterward did not undo that. Gating the whole dropdown (mouse and keyboard alike, below)
   * is simpler than special-casing which actions are "safe" mid-preview, and matches how
   * `isHPFormOpen` already gates the same surface for a different reason.
   */
  const isPreviewingHangingProtocol = loadingPreview || previewedHangingProtocol != null;

  /**
   * Whether a named protocol is applied, which is what `isDefault` answers: the matched HP having no
   * `smid` is how "the Default Hanging Protocol" is represented, not a distinct record.
   *
   * Read once here rather than inside each handler so the memo has a primitive to depend on -- the
   * matched-HP object identity changes on every refetch, which would rebuild the bindings (and so
   * tear down and re-register the Mousetrap instance) far more often than the answer changes.
   */
  const hasAppliedHangingProtocol = !isDefault(hangingProtocol);

  const hpActionShortcutHandlers = useMemo(
    () => ({
      HP_CREATE_NEW: () => {
        if (isHPSurfaceOpen || isPreviewingHangingProtocol) return false;
        setIsDropdownOpen(false);
        open(HP_MODAL_VARIANTS.SAVE);
      },
      // Edit and Reset All act *on* the applied protocol, so they mean nothing when there is none.
      // Their click path is already gated by rendering -- the MoreVert dropdown that owns both items
      // only exists for a non-default protocol -- and the keyboard path had no equivalent gate
      // (VX-4898). Create and Browse above stay ungated on purpose: both are reachable in the default
      // state, Create via the direct "+" button and Browse by clicking the widget.
      //
      // The preview decline is unconditional across all four, though (VX-4248): the mouse path is
      // gated the same way at the dropdown itself, and a hotkey is not a "different, safer" way to
      // reach the same action mid-preview.
      HP_EDIT: () => {
        if (isHPSurfaceOpen || isPreviewingHangingProtocol || !hasAppliedHangingProtocol)
          return false;
        setIsDropdownOpen(false);
        open(HP_MODAL_VARIANTS.UPDATE);
      },
      HP_RESET: () => {
        if (isHPSurfaceOpen || isPreviewingHangingProtocol || !hasAppliedHangingProtocol)
          return false;
        setIsDropdownOpen(false);
        setShowResetModal(true);
      },
      HP_BROWSE: () => {
        if (isHPSurfaceOpen || isPreviewingHangingProtocol) return false;
        setIsDropdownOpen(false);
        open(HP_MODAL_VARIANTS.SEARCH);
      },
    }),
    [open, isHPSurfaceOpen, isPreviewingHangingProtocol, hasAppliedHangingProtocol]
  );

  useHPActionShortcuts(hpActionShortcutHandlers, { context: APOLLO_QUERY_CONTEXT });
  // The same handlers, for presses in viewer/1, which has no widget of its own to bind them (VX-5090).
  useReceiveHPWidgetShortcuts(hpActionShortcutHandlers);

  useEffect(() => {
    onFormOpenChange?.(isHPFormOpen);
  }, [isHPFormOpen, onFormOpenChange]);

  // NOTE: This is implemented to where a users viewports configurations and layout are
  // ALWAYS reset after they submit an HP. This is intentional business logic.
  const onHangingProtocolFormSubmit = useCallback(
    async (hangingProtocolName: string, createdHangingProtocolSmid?: string) => {
      // Only drop the init guard when the HP the viewer should be showing actually changes -- a
      // create, or a switch. On a plain update of the HP already applied, the form has just written
      // the saved steps into the viewer atoms itself, and clearing the guard lets the next
      // getBaseViewerData response re-initialize over that write. The form refetches immediately
      // after the mutation, so if the backend has not made the write visible yet that response is
      // the pre-update HP -- and the freshly added steps disappear from the step bar until a reload
      // (VX-4770). The form deliberately does not clear the guard for exactly this reason; the
      // widget was undoing that a level up.
      if (widgetMode !== HP_MODAL_VARIANTS.UPDATE) {
        invalidateHPInitializationCache();
        invalidateHPSmidGuard();
        // Same condition, and for the same reason: the protocol the viewer should show is changing, so
        // a hold on the outgoing one has to go with it. An unsaved edit holds its protocol against the
        // live match (VX-4872), but a create is the reader asking for a different one, and a hold left
        // standing would make the create look like it did nothing. Deliberately not done on UPDATE:
        // there the held protocol is the one being saved, and it must stay applied.
        clearEditHeldHangingProtocol();
      }
      clearPreviewState();
      // Close before the awaits below, not after them.
      //
      // `close()` is what drops `?hp=create|edit`, and that param -- not the DOM -- is the form's open
      // state: the form unmounts as soon as it submits, but a reload while the param is still set
      // reopens it in both viewer windows with the HP dropdown disabled. Leaving it set across the two
      // GraphQL round trips below therefore leaves a real window where a refresh resurrects a form the
      // reader has finished with, and on a cold tier those round trips are measured in tens of seconds
      // (`GET_HANGING_PROTOCOL` with `cases` is a `getBaseViewerData`-class query).
      //
      // `stepped-hp.e2e` phase 4h4 caught it as "the HP form closed but its URL state still requested
      // it to be open", timing out after 30s. It is the pin below that made this reachable -- without
      // it there was nothing awaited between the submit and the close.
      //
      // Nothing after this point needs the form open, and nothing here reads state `close()` touches:
      // `widgetMode` is captured in this callback's closure, so `setWidgetMode(null)` cannot change
      // what the success notification is built from.
      close();
      // The submit changed the HP record, so the cached payload for the selected HP is stale.
      // Re-resolve it unconditionally -- it is a cache, and refreshing it cannot change which HP is
      // applied (VX-4809). The selection's smid is what keys useHangingProtocol's init guard, and
      // keeping it stable across the refetch is what stops the response re-initializing the step bar
      // from a different protocol and dropping the steps just added (VX-4770).
      await refreshSelectedHangingProtocol();
      // On CREATE, pin the just-created protocol so it becomes the applied one immediately.
      //
      // The server match decides what is applied at **case load**; it does not get to overrule a
      // protocol the reader has just built from the study in front of them (VX-4905). Creating is an
      // explicit choice to look at that protocol, so it is recorded as a selection, which outranks the
      // match in `previewed ?? selected ?? matched`. Two failure modes this closes at once: a criteria
      // tie, where the backend keeps matching an older same-criteria protocol and the create looks like
      // it did nothing (VX-4713), and a protocol the match returns *nothing* for, where the viewer used
      // to fall back to the Default HP wearing the new protocol's steps.
      //
      // Reset is the way back to the match -- it forgets the selection, which is what makes pinning here
      // safe to do unconditionally rather than only on a tie.
      //
      // Awaited before resetToHP so the reset lands on the new protocol's layout, not the stale matched
      // one.
      if (createdHangingProtocolSmid != null) {
        const resolution = await selectHangingProtocol(createdHangingProtocolSmid);
        // The protocol exists on the server -- only resolving it against this case failed, so say that
        // rather than implying the create failed (a second attempt collides on the unique-name check).
        // The success notification is skipped in favour of this one so the two do not contradict.
        if (resolution.outcome === 'failed') {
          // Nothing is applied and the step bar is still showing the steps this save published before
          // the pin was attempted, which is the pair VX-4905 reported. Clear them only when nothing is
          // left to show them for: `clearSteppedHPState` empties the BroadcastChannel-synced step atoms,
          // so calling it while another protocol is still applied would blank *that* protocol's step bar
          // in both windows, and create is reachable from a named HP's dropdown.
          //
          // Asked of the effective protocol rather than the selection alone, since a preview keeps a
          // protocol applied with no selection at all and `clearPreviewState()` above does not drop the
          // preview payload. A `hangingProtocol` that has gone stale since this callback was created errs
          // towards not clearing, which is the safe direction.
          const nothingIsApplied =
            isDefault(hangingProtocol) && selectedHangingProtocolSmid == null;
          if (nothingIsApplied) clearSteppedHPState();
          setErrorMessage(getApplyAfterCreateErrorMessage(hangingProtocolName));
          resetToHP();
          return;
        }
      }
      resetToHP();
      setCompletedMessage(getSuccessNotification(hangingProtocolName, widgetMode));
    },
    [
      clearPreviewState,
      clearEditHeldHangingProtocol,
      clearSteppedHPState,
      hangingProtocol,
      selectedHangingProtocolSmid,
      refreshSelectedHangingProtocol,
      selectHangingProtocol,
      resetToHP,
      close,
      widgetMode,
    ]
  );

  const confirmPreviewedHangingProtocol = useCallback(() => {
    setIsPreviewAccepted(true);
    logger.info(`Confirmed Hanging Protocol ${previewedHangingProtocol?.smid}`);
    confirmPreview();
    // Confirm hands the preview over to the selection through several atoms, and each one reaches
    // the other viewer window as its own message with a render in between. Mid-handoff that window
    // can resolve the previous selection or the stale server match, publish those steps, and use
    // up its once-per-HP publish, so the settled selection never re-publishes and its older steps
    // win on version. Tell it to re-read, as every other HP change does (VX-5494).
    broadcastHPInvalidation();
    resetToHP();
    setHighlightRow(null);
    close();
  }, [confirmPreview, resetToHP, close, previewedHangingProtocol?.smid]);

  const cancelPreviewedHangingProtocol = useCallback(() => {
    if (!isPreviewAccepted) {
      rejectAndRemovePreview();
      if (previewedHangingProtocol != null) {
        logger.info(`Rejected Hanging Protocol ${previewedHangingProtocol.smid}`);
      }
    }
    setHighlightRow(null);
    close();
  }, [close, isPreviewAccepted, rejectAndRemovePreview, previewedHangingProtocol]);

  const onHangingProtocolFormError = useCallback(
    (err: Error) => {
      logger.error(err);
      setErrorMessage(getHangingProtocolErrorMessage(err, widgetMode as SubmitFormIntent));
    },
    [widgetMode]
  );

  const handleDeleteClick = useCallback(async () => {
    if (hangingProtocol == null || hangingProtocol.smid == null) {
      return;
    }
    // Held in a local so the narrowing survives into the async callbacks below.
    const deletedHPSmid = hangingProtocol.smid;
    // Clear the step state as the delete is ISSUED, not when the server confirms it.
    //
    // This used to live in `onCompleted`, which made the step bar the one part of the delete that was
    // not optimistic -- the mutation already sends an `optimisticResponse`. Measured on the deleting
    // window: `deleteHP()` returned at t=42028 and the clear did not land until t=45428, so the bar
    // advertised a protocol that was already gone for 3.4s, and for longer whenever the response was
    // slow (VX-4859, "until reload"). The atoms are BroadcastChannel-synced, so issuing the clear here
    // also clears the other viewer windows at the same moment.
    //
    // Safe against a failed delete: the HP is refetched on the error path, and the init pass that
    // follows re-applies its steps.
    //
    // The hpKey drops this protocol's persisted unsaved-edit record along with the atoms, so a
    // delete does not leave one stranded on disk to be restored on a later load (VX-4803).
    clearSteppedHPState(makeHPKey(currentCaseId, deletedHPSmid));

    // And the viewport overrides those steps were made on, for the same reason and at the same
    // moment. The step atoms going without them is VX-5091: the *flat* bucket was left holding
    // explicit `null` overrides, a null override outranks the protocol base in
    // `generateViewportsConfigurations`, and the atom is persisted -- so the default protocol came
    // back, resolved its series correctly, and rendered an empty grid that a refresh could not fix.
    //
    // Overrides only. `clearAllStepBuckets` would be the obvious reach and it is wrong here: it also
    // empties `dehydratedHangingProtocol`, which only the primary window publishes, so viewer/1 would
    // lose its protocol base with nothing able to restore it. Measured -- that blanked window 1
    // entirely while its step bar still rendered. See `discardStepViewportOverrides`.
    if (currentCaseId != null) discardStepViewportOverrides({ caseId: currentCaseId });

    // Mark the smid deleted and drop the init guards *here*, as the delete is issued, for the same
    // reason the step clear above moved here: the smid goes null optimistically, so everything keyed
    // off it happens now, not when the server answers.
    //
    // These three used to live in `onCompleted`, and the gap was the whole bug (VX-4810 blank grid).
    // The optimistic response nulls `hangingProtocol`, so `hpKey` becomes `<case>:__no_hp__` and
    // `useHangingProtocol`'s init effect runs immediately -- while `lastPublishedHPKey` still names the
    // deleted protocol on this same case. `decideStepInitPass` reads that as a mid-refetch blip and
    // returns `ignore-transient-null`, which deliberately does *not* stamp `steppedHPInitSettledFor`.
    // Meanwhile the layout effect has already nulled the stamp because the key changed. Nothing then
    // re-runs the decision -- clearing the guard in `onCompleted` changes no hook dependency -- so the
    // stamp stays null for good, `activeStepBucketSelector` yields no bucket, and every viewport reads
    // as having neither overrides nor protocol viewports. The entire grid blanks until a reload.
    //
    // Measured on a local stack: after the delete, six consecutive init passes all reported
    // `decision=ignore-transient-null hpKey=<case>:__no_hp__ settledFor=<case>:<deleted smid>`, and no
    // `publish` ever followed.
    //
    // Order matters and is the order of the original `onCompleted` block: mark deleted first, so any
    // in-flight or background response still describing this protocol is dropped by `ignore-deleted`
    // once the guards are down. That is what keeps this from reintroducing VX-4770, whose whole
    // mechanism was a guard-down render re-publishing the pre-delete protocol.
    markHangingProtocolDeleted(deletedHPSmid);
    invalidateHPInitializationCache();
    // Deleting the active HP is a legitimate transition to null/default -- clear the smid-write guard
    // so it isn't mistaken for a transient refetch blip and skipped, which would leave
    // activeHangingProtocolSmidState pointing at the deleted HP (VX-4615 review).
    invalidateHPSmidGuard();

    // Tell the other windows at the same moment. The clear alone is not enough: viewer/1 still holds
    // the pre-delete protocol until its own refetch lands, and its re-init publishes that 1-step copy
    // at a winning version, which broadcast-syncs straight back into this window and puts the bar
    // back. Measured: v0 cleared at t=52577 and read false at t=52833, then v1 wrote n=1 and the bar
    // returned ~1s later. Broadcasting the deleted smid here makes v1 mark it deleted before it can
    // publish, so the stale response is dropped instead of winning (VX-4859).
    //
    // The post-confirmation broadcast below stays: it is what triggers the other windows' refetch.
    broadcastHPInvalidation({ deletedHPSmid });
    await deleteHangingProtocol({
      variables: { id: deletedHPSmid },
      optimisticResponse: {
        __typename: 'Mutation',
        deleteHangingProtocol: {
          __typename: 'HangingProtocolDeleted',
          smid: hangingProtocol.smid,
        },
      },
      onCompleted: async (data) => {
        if (hangingProtocol?.name == null) {
          return;
        }

        if (isPhase4 && hangingProtocol) {
          analytics.track(viewer.usr.hangingProtocolDeleted, {
            hanging_protocol_name: hangingProtocol.name,
            hanging_protocol_smid: hangingProtocol.smid,
            intent: 'delete',
            hanging_protocol_payload: {
              name: hangingProtocol.name,
              study_descriptions: hangingProtocol.studyDescriptions ?? [],
              number_of_priors: hangingProtocol.numberOfPriors ?? 0,
              study_criteria: hangingProtocol.studyCriteria ?? null,
              layouts: hangingProtocol.data?.layouts ?? [],
              viewports_configurations:
                hangingProtocol.viewportsConfigurations?.map((vc) => ({
                  id: vc.id,
                  view_type: vc.configuration.viewType,
                  series_descriptions: vc.configuration.seriesDescriptions ?? [],
                  series_criteria: vc.configuration.seriesCriteria ?? null,
                  comparison_index: vc.configuration.comparisonIndex,
                })) ?? [],
            },
          });
        }

        setCompletedMessage(`Hanging protocol '${hangingProtocol.name}' deleted successfully.`);
        close();
        // The step state, the deleted-smid mark and both init guards were all handled when the delete
        // was issued, above (VX-4859 moved the step clear there; VX-4810 moved the mark and the guards,
        // because the optimistic smid flip beat them and left the grid blank). Only the cache
        // invalidation and refetch belong here, on confirmation.
        await invalidateHangingProtocolMatch();
        await refetch();
        // Signal other viewer windows to refetch so they pick up the deletion. They need the smid
        // too -- each window has its own query lifecycle and its own late responses to drop.
        broadcastHPInvalidation({ deletedHPSmid });
      },
      update(cache, { data }) {
        if (data == null) {
          logger.warn('Unable to delete hanging protocol, missing update data argument');
          return;
        }

        // Reject preview if the deleted hanging protocol matches the deleted one
        if (data.deleteHangingProtocol?.smid === previewedHangingProtocol?.smid) {
          rejectAndRemovePreview();
        }

        // A selected HP is no longer backed by a real record once deleted — forget the selection
        // (not just its cached payload) so originalHangingProtocol falls back to the (refetched)
        // server-matched HP instead of keeping the deleted one applied indefinitely.
        if (data.deleteHangingProtocol?.smid === selectedHangingProtocolSmid) {
          clearSelectedHangingProtocol();
        }

        // Same reasoning one layer down: an unsaved edit holds its protocol against the match
        // (VX-4872), and a hold naming the protocol just deleted would outrank the refetched match and
        // keep a record that no longer exists applied. Unconditional -- the hold is per case, and the
        // only protocol it can be naming here is the one being deleted.
        clearEditHeldHangingProtocol();
      },
    });
  }, [
    clearSteppedHPState,
    discardStepViewportOverrides,
    currentCaseId,
    close,
    deleteHangingProtocol,
    hangingProtocol,
    invalidateHangingProtocolMatch,
    refetch,
    isPhase4,
    previewedHangingProtocol?.smid,
    rejectAndRemovePreview,
    selectedHangingProtocolSmid,
    clearSelectedHangingProtocol,
    clearEditHeldHangingProtocol,
  ]);

  // Type annotation ensures properties aren't inferred as optional.
  // Conditional ensures `widgetMode` is narrowed to `SubmitFormIntent`.
  const hangingProtocolsFormProps: HangingProtocolsFormProps | null = isSubmitFormIntent(widgetMode)
    ? {
        hangingProtocol,
        onSubmit: onHangingProtocolFormSubmit,
        onError: onHangingProtocolFormError,
        onClose: close,
        layouts,
        studyDescriptions,
        groupedStudies: groupStudyIds,
        mode: widgetMode,
        phase,
      }
    : null;

  return (
    <>
      <Tooltip
        content={hangingProtocol && !isHPFormOpen ? 'Hanging Protocol' : null}
        visible={isWidgetHovered && !(isSaveButtonHovered && isDefault(hangingProtocol))}
      >
        <Menu
          disabled={isHPFormOpen || isPreviewingHangingProtocol}
          placement="bottom-end"
          visible={isDropdownOpen}
          onClickOutside={() => {
            setIsDropdownOpen(false);
            close();
          }}
          css={css`
            margin-top: ${Spacings.xsmall}rem;
            margin-bottom: ${Spacings.xsmall}rem;
            opacity: 0.7;
          `}
          appendTo={() => document.body}
          data-testid="hanging-protocol-menu"
          render={() => (
            <>
              <MenuItem css={menuItemCss} onClick={() => {}} disabled>
                <Text color="secondary">Hanging protocols</Text>
              </MenuItem>
              <MenuItem
                title="Browse hanging protocols"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.SEARCH);
                }}
                data-testid="browse-hanging-protocols"
                data-analytics-name="hanging-protocol-browse-button"
              >
                <SearchOutlined css={iconCss} fontSize="small" />
                <Text>Browse</Text>
              </MenuItem>
              <MenuItem
                title="Create new hanging protocol from current viewports"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.SAVE);
                }}
                data-testid="create-hanging-protocols"
                data-analytics-name="hanging-protocol-create-button"
              >
                <AddCircle css={iconCss} fontSize="small" />

                <Text>Create</Text>
              </MenuItem>
              <MenuItem
                title="Edit hanging protocol"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.UPDATE);
                }}
                data-testid="update"
                data-analytics-name="hanging-protocol-update-button"
              >
                <BorderColor css={iconCss} fontSize="small" />
                Edit
              </MenuItem>
              <MenuItem
                title="Reset viewports to hanging protocol defaults"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  setShowResetModal(true);
                }}
                data-testid="reset-hanging-protocol"
                data-analytics-name="hanging-protocol-reset-button"
              >
                <Refresh css={iconCss} fontSize="small" />
                Reset All
              </MenuItem>
              {!isPhase4 && (
                <MenuItem
                  title="Only update hanging protocol layout from current configuration"
                  css={menuItemCss}
                  onClick={(evt) => {
                    setIsDropdownOpen(false);
                    quickUpdate(evt);
                  }}
                  data-testid="update-layout-hanging-protocols"
                  data-analytics-name="hanging-protocol-update-layout-button"
                >
                  <Replay css={iconCss} fontSize="small" />
                  Update layout only
                </MenuItem>
              )}
              <MenuItem
                title="Delete hanging protocol"
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.DELETE);
                }}
                data-testid="delete"
                css={menuItemCss}
              >
                <DeleteForeverIcon css={iconCss} fontSize="small" />
                Delete
              </MenuItem>
            </>
          )}
        >
          <BaseWidget
            data-testid="hanging-protocol-widget"
            placeholder="Hanging Protocol"
            text={!loadingHangingProtocol ? hangingProtocol?.name : 'Loading...'}
            disabled={isHPFormOpen}
            onMouseEnter={() => setIsWidgetHovered(true)}
            onMouseLeave={() => setIsWidgetHovered(false)}
            onClick={() => {
              if (isDropdownOpen) {
                setIsDropdownOpen(false);
              } else {
                open(HP_MODAL_VARIANTS.SEARCH);
              }
            }}
            accessories={
              <>
                <AccessoryButton
                  onClick={() => open(HP_MODAL_VARIANTS.SEARCH)}
                  data-testid="hanging-protocol-search"
                  disabled={isHPFormOpen || isPreviewingHangingProtocol}
                  icon={<SearchIcon />}
                />
                {!isDefault(hangingProtocol) &&
                  (isPreviewingHangingProtocol ? (
                    <Tooltip content="Actions are disabled while previewing a hanging protocol">
                      <AccessoryButton
                        icon={<VisibilityIcon />}
                        data-testid="hanging-protocol-previewing"
                        disabled
                      />
                    </Tooltip>
                  ) : (
                    <AccessoryButton
                      icon={<MoreVert />}
                      data-testid="hanging-protocol-dropdown"
                      disabled={isHPFormOpen}
                      onClick={(evt) => {
                        evt.stopPropagation();
                        setIsDropdownOpen((open) => !open);
                      }}
                    />
                  ))}
                {isDefault(hangingProtocol) && (
                  <Tooltip
                    content={
                      <>
                        Create new hanging protocol
                        <br />
                        from current viewports
                      </>
                    }
                    onTrigger={() => setIsSaveButtonHovered(true)}
                    onUntrigger={() => setIsSaveButtonHovered(false)}
                  >
                    <AccessoryButton
                      onClick={(evt) => {
                        evt.stopPropagation();
                        open(HP_MODAL_VARIANTS.SAVE);
                      }}
                      data-testid="hanging-protocol-save-button"
                      data-analytics-name="hanging-protocol-save-button"
                      disabled={isHPFormOpen || isPreviewingHangingProtocol}
                      icon={<AddCircle />}
                    />
                  </Tooltip>
                )}
              </>
            }
          />
        </Menu>
      </Tooltip>
      {isPhase4 ? (
        <Sidebar
          activeSidebarId={HANGING_PROTOCOL_SIDEBAR_ID}
          isOpen={shouldOpenHangingProtocolsForm && isOpen}
          showOverlay={showSidebarOverlay}
        >
          {hangingProtocolsFormProps != null && (
            <HangingProtocolsFormV4 className="MuiDialog-paper" {...hangingProtocolsFormProps} />
          )}
        </Sidebar>
      ) : phase === HANGING_PROTOCOL_PHASE.THREE ? (
        <Sidebar
          activeSidebarId={HANGING_PROTOCOL_SIDEBAR_ID}
          isOpen={shouldOpenHangingProtocolsForm && isOpen}
          showOverlay={showSidebarOverlay}
        >
          {hangingProtocolsFormProps != null && (
            <HangingProtocolsForm className="MuiDialog-paper" {...hangingProtocolsFormProps} />
          )}
        </Sidebar>
      ) : (
        <Dialog open={shouldOpenHangingProtocolsForm && isOpen} onClose={close}>
          {hangingProtocolsFormProps != null && (
            <HangingProtocolsForm {...hangingProtocolsFormProps} />
          )}
        </Dialog>
      )}
      {widgetMode === HP_MODAL_VARIANTS.DELETE && isOpen && (
        <HPConfirmationModal
          {...makeDeleteHPModalContent(hangingProtocol?.name ?? '')}
          // The e2e page objects have always driven deletion by this id; keeping it means this swap is invisible to
          // them.
          primaryActionTestId="delete-hanging-protocol-button"
          onPrimaryAction={handleDeleteClick}
          onSecondaryAction={close}
          onClose={close}
        />
      )}
      {pendingPreviewSmid != null && (
        <HPConfirmationModal
          {...PREVIEW_WITH_UNSAVED_CHANGES_MODAL}
          onPrimaryAction={handleConfirmDiscardAndPreview}
          onSecondaryAction={handleCancelDiscardPreview}
          onClose={handleCancelDiscardPreview}
        />
      )}
      {showResetModal && (
        <HPConfirmationModal
          {...RESET_ALL_MODAL}
          onPrimaryAction={handleResetAll}
          onSecondaryAction={() => setShowResetModal(false)}
          onClose={() => setShowResetModal(false)}
        />
      )}
      {isOpen && widgetMode === HP_MODAL_VARIANTS.SEARCH && (
        <DraggableContainer
          dragConstraintRef={viewerRef}
          css={css`
            margin-top: 3rem;
          `}
        >
          <Stack
            css={`
              padding: 12px 32px;
              min-width: 458px;
              min-height: 501px;
              border-radius: ${rootTheme.radius.sm}px;
              background-color: ${Colors.gray3};
              color: ${Colors.gray8};
            `}
            data-testid="search-hanging-protocol-modal"
          >
            <Stack
              css={`
                display: flex;
                flex-direction: column;
                justify-content: space-between;
              `}
            >
              <Text variant="display1">Select Hanging Protocol</Text>
              <Text
                css={`
                  padding: 0;
                `}
              >
                {POWERED_BY_SIRONA_CLASSIFICATIONS}
              </Text>
              <Input
                icon={<SearchIcon />}
                placeholder="Enter search term"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="search"
                autoFocus={true}
                css={`
                  margin-top: 18px;
                `}
              />

              <Table
                data={searchItems}
                columns={columns}
                Row={Row}
                // @ts-expect-error Table expects boolean but Row component uses number for index comparison
                highlightRow={highlightRow}
                showHeaderSeparators={true}
                loading={loading}
                css={`
                  background-color: ${Colors.gray1};
                  border-radius: ${rootTheme.radius.sm}px;
                  margin: 18px 0 6px 0;
                  flex: 1;
                  min-height: 0;
                  padding: 10px;
                  max-height: 264px;
                  min-height: 264px;
                `}
              />

              <Text variant="body2">
                Click confirm to keep or cancel to revert to previous settings.
              </Text>

              <Stack css="margin: 24px 0 35px 0; justify-content: flex-end; gap: 18px;">
                <Button
                  onClick={cancelPreviewedHangingProtocol}
                  variant="ghost"
                  data-testid="cancel-previewed-hanging-protocol"
                  disabled={loadingPreview}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-previewed-hanging-protocol"
                  onClick={confirmPreviewedHangingProtocol}
                  variant="primary"
                  disabled={previewedHangingProtocol == null || loadingPreview}
                >
                  Confirm
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </DraggableContainer>
      )}
      <GenericSnackBar
        open={completedMessage != null}
        msg={completedMessage ?? ''}
        handleClose={() => setCompletedMessage(null)}
        severity="default"
        data-testid="success-hanging-protocol"
        ContentProps={SNACKBAR_CONTENT_PROPS}
        icon={completedMessage?.includes('delete') ? 'redDeleteForever' : 'greenCheck'}
      />
      <GenericSnackBar
        open={errorMessage != null}
        msg={errorMessage ?? ''}
        handleClose={unsetError}
        severity="default"
        icon="redWarning"
        data-testid="error-hanging-protocol"
        ContentProps={SNACKBAR_CONTENT_PROPS}
      />
    </>
  );
};

const MemoHangingProtocolWidget = memo(HangingProtocolWidget);

export default MemoHangingProtocolWidget;

export function useShouldShowHangingProtocolWidget() {
  const viewerId = useViewerId();
  return viewerId === '0';
}

HangingProtocolWidget.displayName = 'HangingProtocolWidget';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(HangingProtocolWidget);
